import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Wand2, Camera, Upload, ImagePlus, UserCircle, ArrowLeft, Loader2, RefreshCw, Check, Lock, Coins, Crown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STYLES = ["Masquerade", "Vampire Royalty", "Golden Goddess", "Boudoir Glam", "Art-Deco Icon",
  "Cyberpunk Muse", "Old Hollywood", "Gothic Aristocrat", "Renaissance Muse", "Leather Noir"];

const ART_STYLES = [
  { id: "3d", label: "3D Animated" },
  { id: "anime", label: "Anime" },
  { id: "illustration", label: "Illustrated" },
  { id: "cartoon", label: "Cartoon" },
];

const COLORS = [
  { name: "Gold", hex: "#D4AF37" }, { name: "Crimson", hex: "#E63946" }, { name: "Emerald", hex: "#2ecc71" },
  { name: "Sapphire", hex: "#3b82f6" }, { name: "Amethyst", hex: "#9b5de5" }, { name: "Rose", hex: "#ff6b9d" },
  { name: "Silver", hex: "#c0c0c0" }, { name: "Neon Pink", hex: "#ff2d95" },
];

const FEATURES = ["Glowing eyes", "Gold tattoos", "Sparkling jewels", "Angel wings", "Neon glow",
  "Face gems", "Golden halo", "Butterfly accents", "Smoky aura", "Flower crown"];

// Downscale an image element / video frame to a JPEG data URL (max 1024px)
function toDataUrl(source, w, h) {
  const max = 1024;
  let nw = w, nh = h;
  if (w > h && w > max) { nw = max; nh = Math.round((h * max) / w); }
  else if (h > max) { nh = max; nw = Math.round((w * max) / h); }
  const canvas = document.createElement("canvas");
  canvas.width = nw; canvas.height = nh;
  canvas.getContext("2d").drawImage(source, 0, 0, nw, nh);
  return canvas.toDataURL("image/jpeg", 0.85);
}

export default function Avatar() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [photo, setPhoto] = useState(null); // data URL
  const [camOn, setCamOn] = useState(false);
  const [style, setStyle] = useState("Masquerade");
  const [renderStyle, setRenderStyle] = useState("3d");
  const [color, setColor] = useState("Gold");
  const [features, setFeatures] = useState([]);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [avatarPath, setAvatarPath] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [outfits, setOutfits] = useState([]);
  const [selectedOutfit, setSelectedOutfit] = useState(null);
  const [outfitCoins, setOutfitCoins] = useState(0);
  const [buying, setBuying] = useState(null);
  const [bundle, setBundle] = useState(null);

  const loadGallery = () => api.post("/avatar/generations").then((r) => setGallery(r.data)).catch(() => {});
  const loadOutfits = () => api.get("/outfits").then((r) => {
    setOutfits(r.data.outfits);
    setOutfitCoins(r.data.coins_balance);
    setBundle({ price: r.data.bundle_price, full: r.data.bundle_full_price, owned: r.data.bundle_owned });
  }).catch(() => {});
  useEffect(() => { loadGallery(); loadOutfits(); return () => stopCam(); }, []);

  const pickOutfit = (o) => { if (o.owned) setSelectedOutfit((cur) => (cur === o.id ? null : o.id)); };

  const buyOutfit = async (o) => {
    if (buying) return;
    if (!window.confirm(`Unlock "${o.name}" for ${o.price} Empire Coins?`)) return;
    setBuying(o.id);
    try {
      const { data } = await api.post("/outfits/buy", { outfit_id: o.id });
      setOutfitCoins(data.coins_balance);
      setSelectedOutfit(o.id);
      await loadOutfits();
      await refreshUser();
      toast.success(`${o.name} unlocked 👑`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Purchase failed");
    } finally { setBuying(null); }
  };

  const buyBundle = async () => {
    if (buying || !bundle) return;
    if (!window.confirm(`Unlock ALL outfits for ${bundle.price} Empire Coins? (worth ${bundle.full})`)) return;
    setBuying("bundle");
    try {
      const { data } = await api.post("/outfits/buy-bundle");
      setOutfitCoins(data.coins_balance);
      await loadOutfits();
      await refreshUser();
      toast.success("Every outfit unlocked — dress up like royalty 👑");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Purchase failed");
    } finally { setBuying(null); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => setPhoto(toDataUrl(img, img.width, img.height));
    img.src = URL.createObjectURL(file);
    stopCam();
  };

  const startCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      setCamOn(true);
      setPhoto(null);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); } }, 100);
    } catch (e) { toast.error("Couldn't access camera. Check permissions or use upload."); }
  };

  const stopCam = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setCamOn(false);
  };

  const capture = () => {
    const v = videoRef.current;
    if (!v) return;
    setPhoto(toDataUrl(v, v.videoWidth, v.videoHeight));
    stopCam();
  };

  const toggleFeature = (f) => setFeatures((cur) => cur.includes(f) ? cur.filter((x) => x !== f) : (cur.length < 4 ? [...cur, f] : cur));

  const generate = async () => {
    if (!photo) { toast.error("Take or upload a photo first"); return; }
    setLoading(true);
    setAvatarPath(null);
    try {
      const { data } = await api.post("/avatar/generate", { style, color, features, custom, photo_base64: photo, render_style: renderStyle, outfit_id: selectedOutfit });
      const jobId = data.job_id;
      // Poll the job (generation runs in the background to avoid proxy timeouts)
      let done = false;
      for (let i = 0; i < 45 && !done; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: job } = await api.get(`/avatar/job/${jobId}`);
        if (job.status === "done") {
          setAvatarPath(job.avatar_path);
          await refreshUser();
          loadGallery();
          toast.success("Your avatar is ready ✨");
          done = true;
        } else if (job.status === "error") {
          toast.error(job.error || "Generation failed");
          done = true;
        }
      }
      if (!done) toast.error("This is taking longer than usual — please try again.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed");
    } finally { setLoading(false); }
  };

  const fileUrl = (p) => `${BACKEND}/api/avatar/file/${p}`;
  // Owner preview of not-yet-published avatars needs a token; published profile photos are public.
  const urlFor = (p) => `${fileUrl(p)}?auth=${localStorage.getItem("pe_token") || ""}`;

  const apply = async (mode, path) => {
    const url = fileUrl(path);
    const existing = (user?.photos || []).filter((p) => p !== url);
    const photos = mode === "profile" ? [url, ...existing] : [...existing, url];
    try {
      await api.put("/profile", { photos });
      await refreshUser();
      toast.success(mode === "profile" ? "Set as your profile picture 👑" : "Added to your photos 🎭");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-12 max-w-5xl mx-auto">
      <button data-testid="avatar-back" onClick={() => navigate("/profile")} className="text-[#A0A0A0] hover:text-white flex items-center gap-2 mb-4">
        <ArrowLeft size={18} /> Back to profile
      </button>
      <div className="overline mb-1">Avatar Atelier</div>
      <h2 className="font-heading text-3xl md:text-4xl mb-2 flex items-center gap-3"><Sparkles className="text-[#D4AF37]" /> Glam Up Your Selfie</h2>
      <p className="text-[#A0A0A0] text-sm mb-8 max-w-xl">Snap or upload a photo of your face, pick a style, colour & features — and we'll transform it into a stunning avatar that still looks like you. Free for everyone.</p>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Left: photo + controls */}
        <div className="space-y-5">
          {/* Photo input */}
          <div>
            <div className="text-xs text-[#A0A0A0] mb-2">1. Your photo</div>
            <div className="pe-card rounded-2xl aspect-square flex items-center justify-center overflow-hidden relative">
              {camOn ? (
                <video data-testid="avatar-video" ref={videoRef} className="w-full h-full object-cover" playsInline muted />
              ) : photo ? (
                <img data-testid="avatar-selfie" src={photo} alt="selfie" className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-[#555]"><Camera size={44} /><span className="text-sm">No photo yet</span></div>
              )}
            </div>
            <div className="flex gap-3 mt-3">
              {camOn ? (
                <>
                  <button data-testid="avatar-capture" onClick={capture} className="btn-gold rounded-full px-5 py-2 text-sm flex-1 flex items-center justify-center gap-2"><Camera size={16} /> Capture</button>
                  <button data-testid="avatar-cam-cancel" onClick={stopCam} className="btn-outline-gold rounded-full px-4 py-2 text-sm">Cancel</button>
                </>
              ) : (
                <>
                  <button data-testid="avatar-open-cam" onClick={startCam} className="btn-outline-gold rounded-full px-4 py-2 text-sm flex-1 flex items-center justify-center gap-2"><Camera size={16} /> Use camera</button>
                  <button data-testid="avatar-upload-btn" onClick={() => fileRef.current?.click()} className="btn-outline-gold rounded-full px-4 py-2 text-sm flex-1 flex items-center justify-center gap-2"><Upload size={16} /> Upload</button>
                  <input ref={fileRef} data-testid="avatar-file" type="file" accept="image/*" onChange={onFile} className="hidden" />
                </>
              )}
            </div>
          </div>

          {/* Art style */}
          <div>
            <div className="text-xs text-[#A0A0A0] mb-2">2. Art style</div>
            <div className="flex flex-wrap gap-2">
              {ART_STYLES.map((a) => (
                <button key={a.id} data-testid={`avatar-render-${a.id}`} onClick={() => setRenderStyle(a.id)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors ${renderStyle === a.id ? "gold-gradient text-black font-medium" : "border border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>{a.label}</button>
              ))}
            </div>
          </div>

          {/* Style */}
          <div>
            <div className="text-xs text-[#A0A0A0] mb-2">3. Vibe</div>
            <div className="flex flex-wrap gap-2">
              {STYLES.map((s) => (
                <button key={s} data-testid={`avatar-style-${s}`} onClick={() => setStyle(s)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors ${style === s ? "gold-gradient text-black font-medium" : "border border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>{s}</button>
              ))}
            </div>
          </div>

          {/* Outfit Shop */}
          <div>
            <div className="text-xs text-[#A0A0A0] mb-2 flex items-center justify-between">
              <span>4. Choose your outfit <span className="text-[#555]">(dress up your queen)</span></span>
              <span data-testid="outfit-coin-balance" className="flex items-center gap-1 text-[#D4AF37]"><Coins size={12} /> {outfitCoins}</span>
            </div>

            {/* Unlock-all bundle */}
            {bundle && !bundle.owned && (
              <div data-testid="outfit-bundle-banner"
                className="rounded-xl p-3 mb-3 flex items-center justify-between gap-3 border border-[#D4AF37]/40"
                style={{ background: "linear-gradient(100deg,#1c1608,#0A0A0A)" }}>
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium flex items-center gap-1.5"><Crown size={14} className="text-[#D4AF37]" /> Unlock the whole wardrobe</div>
                  <div className="text-[11px] text-[#A0A0A0]">
                    All outfits — <span className="text-[#D4AF37] font-medium">{bundle.price}</span>
                    <span className="line-through ml-1 text-[#666]">{bundle.full}</span> coins
                  </div>
                </div>
                <button data-testid="outfit-bundle-buy" onClick={buyBundle} disabled={buying === "bundle"}
                  className="btn-gold rounded-full px-4 py-2 text-xs flex items-center gap-1.5 flex-shrink-0 disabled:opacity-50">
                  {buying === "bundle" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Unlock all
                </button>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="outfit-shop">
              {outfits.map((o) => {
                const active = selectedOutfit === o.id;
                return (
                  <div key={o.id} data-testid={`outfit-${o.id}`}
                    className={`rounded-xl overflow-hidden border transition-all ${active ? "border-[#D4AF37] ring-1 ring-[#D4AF37]" : "border-[#2A2A2A]"}`}>
                    <div className="aspect-[3/4] relative bg-[#111]">
                      <img src={o.image} alt={o.name} loading="lazy"
                        className={`w-full h-full object-cover ${o.owned ? "" : "brightness-[0.55]"}`} />
                      {!o.owned && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center border border-[#D4AF37]/50">
                            <Lock size={14} className="text-[#D4AF37]" />
                          </div>
                        </div>
                      )}
                      {active && (
                        <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full gold-gradient flex items-center justify-center">
                          <Check size={13} className="text-black" />
                        </div>
                      )}
                      {o.free && (
                        <div className="absolute top-1.5 left-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-[#2ecc71]/90 text-black font-medium">Free</div>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-[11px] text-white truncate mb-1.5">{o.name}</div>
                      {o.owned ? (
                        <button data-testid={`outfit-select-${o.id}`} onClick={() => pickOutfit(o)}
                          className={`w-full text-[10px] px-2 py-1.5 rounded-full flex items-center justify-center gap-1 transition-colors ${active ? "gold-gradient text-black font-medium" : "border border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>
                          {active ? <><Check size={10} /> Wearing</> : "Wear"}
                        </button>
                      ) : (
                        <button data-testid={`outfit-buy-${o.id}`} onClick={() => buyOutfit(o)} disabled={buying === o.id}
                          className="w-full text-[10px] px-2 py-1.5 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center gap-1 disabled:opacity-50">
                          {buying === o.id ? <Loader2 size={10} className="animate-spin" /> : <Coins size={10} />} {o.price}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Colour */}
          <div>
            <div className="text-xs text-[#A0A0A0] mb-2">5. Accent colour</div>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button key={c.name} data-testid={`avatar-color-${c.name}`} onClick={() => setColor(c.name)} title={c.name}
                  className={`w-8 h-8 rounded-full border-2 transition-transform ${color === c.name ? "scale-110 border-white" : "border-transparent"}`}
                  style={{ background: c.hex }} />
              ))}
            </div>
          </div>

          {/* Features */}
          <div>
            <div className="text-xs text-[#A0A0A0] mb-2">6. Make it pop <span className="text-[#555]">(up to 4)</span></div>
            <div className="flex flex-wrap gap-2">
              {FEATURES.map((f) => (
                <button key={f} data-testid={`avatar-feature-${f}`} onClick={() => toggleFeature(f)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors flex items-center gap-1 ${features.includes(f) ? "gold-gradient text-black font-medium" : "border border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>
                  {features.includes(f) && <Check size={12} />} {f}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs text-[#A0A0A0] mb-1">7. Extra details (optional)</div>
            <input data-testid="avatar-custom" value={custom} maxLength={200}
              onChange={(e) => setCustom(e.target.value)} placeholder="e.g. emerald eyes, playful smirk, star freckles"
              className="pe-input w-full rounded-lg px-4 py-3 text-sm" />
          </div>

          <button data-testid="avatar-generate" onClick={generate} disabled={loading || !photo}
            className="btn-gold rounded-full px-6 py-3 text-sm w-full flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Conjuring…</> : <><Wand2 size={16} /> Generate my avatar</>}
          </button>
        </div>

        {/* Right: result */}
        <div>
          <div className="text-xs text-[#A0A0A0] mb-2">Your avatar</div>
          <div className="pe-card rounded-2xl aspect-square flex items-center justify-center overflow-hidden relative">
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div key="load" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex flex-col items-center gap-3 text-[#A0A0A0]">
                  <Loader2 size={36} className="animate-spin text-[#D4AF37]" />
                  <span className="text-sm">Painting your portrait…</span>
                </motion.div>
              ) : avatarPath ? (
                <motion.img key={avatarPath} data-testid="avatar-preview" src={urlFor(avatarPath)} alt="Your avatar"
                  initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                  className="w-full h-full object-cover" />
              ) : (
                <motion.div key="empty" className="flex flex-col items-center gap-2 text-[#555]">
                  <UserCircle size={56} /> <span className="text-sm">Your avatar appears here</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {avatarPath && !loading && (
            <>
              <div className="flex gap-3 mt-4">
                <button data-testid="avatar-set-profile" onClick={() => apply("profile", avatarPath)}
                  className="btn-gold rounded-full px-4 py-2 text-sm flex-1 flex items-center justify-center gap-2"><UserCircle size={16} /> Use as profile pic</button>
                <button data-testid="avatar-add-photos" onClick={() => apply("alongside", avatarPath)}
                  className="btn-outline-gold rounded-full px-4 py-2 text-sm flex-1 flex items-center justify-center gap-2"><ImagePlus size={16} /> Add to my photos</button>
              </div>
              <button data-testid="avatar-regenerate" onClick={generate}
                className="text-[#A0A0A0] hover:text-white text-xs flex items-center gap-1 mt-3 mx-auto"><RefreshCw size={12} /> Regenerate</button>
            </>
          )}
        </div>
      </div>

      {/* Past creations */}
      {gallery.length > 0 && (
        <div className="mt-12">
          <h3 className="font-heading text-2xl mb-1">Your wardrobe</h3>
          <p className="text-[#A0A0A0] text-sm mb-4">Tap to preview, or set any look as your profile picture — it shows off across the whole app.</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {gallery.map((g, i) => {
              const isCurrent = ((user?.photos || [])[0] || "").includes(g.path);
              return (
                <div key={g.path} data-testid="avatar-gallery-item"
                  className={`group relative aspect-square rounded-xl overflow-hidden border transition-colors ${isCurrent ? "border-[#D4AF37]" : "border-[#2A2A2A] hover:border-[#D4AF37]"}`}>
                  <img src={urlFor(g.path)} alt="" onClick={() => setAvatarPath(g.path)}
                    className="w-full h-full object-cover cursor-pointer" />
                  {isCurrent && (
                    <div className="absolute top-1.5 left-1.5 text-[9px] px-1.5 py-0.5 rounded-full gold-gradient text-black font-medium flex items-center gap-1">
                      <Crown size={9} /> Profile
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 p-1.5 flex flex-col gap-1 bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <button data-testid={`gallery-set-profile-${i}`} onClick={() => apply("profile", g.path)}
                      className="text-[9px] px-1.5 py-1 rounded-full gold-gradient text-black font-medium flex items-center justify-center gap-1">
                      <UserCircle size={10} /> Profile pic
                    </button>
                    <button data-testid={`gallery-add-photos-${i}`} onClick={() => apply("alongside", g.path)}
                      className="text-[9px] px-1.5 py-1 rounded-full border border-[#D4AF37]/60 text-[#D4AF37] bg-black/40 flex items-center justify-center gap-1">
                      <ImagePlus size={10} /> Add
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
